import React, { useMemo, useRef } from 'react';

import { useHistory } from 'react-router-dom';
import KebabMenu from 'screens/Levelup/LevelUpCF/gridComponents/KebabMenu';
import { columnToCellMetaMapper } from 'screens/Levelup/LevelUpCF/helpers';
import { Loader } from 'screens/analytics/commonComponents';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';

import { pushFeedbackParams } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendOpenedFeedbackReceivedPanelEvent } from 'constants/mixpanelHelpers/reviewsEvents';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';
import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { Paper } from 'uiToolkitV1/Paper';

import useFeedbackReviewPeriodSummaryData from './hooks/dataHooks';

const FeedbackReviewPeriodSummary = () => {
  const { employeeId } = useReviewFormParams();
  const history = useHistory();
  const gridRef = useRef();

  const { data, isLoading, isIdle } = useFeedbackReviewPeriodSummaryData({
    employeeId,
    limit: 500,
    offset: 0,
  });

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      sortable: true,
      menuTabs: ['filterMenuTab'],
      headerComponent: AgGridHeader,
    }),
    []
  );

  const columnDefs = useMemo(() => {
    const dynamicColumns =
      data?.columns.map(({ id, displayName, uuidType }) =>
        columnToCellMetaMapper({ id, displayName, uuidType, type: 'RECEIVED' })
      ) ?? [];

    const kebabMenuColumn = {
      width: 80,
      flex: 1,
      colId: 'kebab-menu',
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params) => <KebabMenu params={params} type="RECEIVED" />,
      valueGetter: (params) => params.data,
      field: '',
      headerName: '',
      lockPosition: 'right',
      pinned: 'right',
      suppressHeaderMenuButton: true,
      cellClass: 'locked-col',
    };

    return [...dynamicColumns, kebabMenuColumn];
  }, [data]);

  const rowClickHandler = ({ data: rowData, column }) => {
    const { uuid: feedbackId } = rowData.comment ?? {};

    if (column.colId === 'kebab-menu') {
      //  To prevent event bubbling.
    } else {
      sendOpenedFeedbackReceivedPanelEvent({
        eventProperties: {
          empId: employeeId,
          source: 'review form',
          feedbackType: rowData?.feedbackType?.value === 'CONSTRUCTIVE' ? 'Feedback' : 'Praise',
        },
      });
      pushFeedbackParams({
        history,
        formType: 'SHOW_RECEIVED',
        typeMapped: true,
        feedbackId,
      });
    }
  };

  if (isLoading || isIdle) {
    return (
      <Paper p={3}>
        <Loader />
      </Paper>
    );
  }

  return (
    <Paper ref={gridRef} pt="22px" className={`ag-theme-alpine goals-ag-grid ${isMobileView() ? 'mobile-style' : ''}`}>
      <AgGridWrapper
        pagination
        suppressPaginationPanel={
          data?.values?.length < 6 || data?.values?.length === undefined || data?.values?.length === 0
        }
        domLayout="autoHeight"
        animateRows={false}
        rowData={data?.values}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        style={{ width: '100%', height: '100%' }}
        rowHeight={74}
        rowStyle={{ cursor: 'pointer' }}
        paginationPageSize={isMobileView() ? 2 : 10}
        sortable
        onCellClicked={rowClickHandler}
        cacheQuickFilter
      />
    </Paper>
  );
};

export default FeedbackReviewPeriodSummary;
